// src/pages/OnboardingListPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react';
import Navigation from '../components/common/Navigation';
import adminOnboardingService from '../services/adminOnboardingService';
import { OnboardingSummary, OnboardingStats } from '../types/onboarding';
import './OnboardingListPage.css';

const OnboardingListPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [onboardings, setOnboardings] = useState<OnboardingSummary[]>([]);
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadOnboardings();
  }, [page, statusFilter, search]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadOnboardings = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminOnboardingService.getOnboardingList({
        page,
        limit: 10,
        status: statusFilter,
        search,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      setOnboardings(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load onboardings');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminOnboardingService.getOnboardingStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  };

  const handleRefresh = () => {
    loadOnboardings();
    loadStats();
  };

  const handleViewDetails = (userId: string) => {
    navigate(`/onboarding/${userId}`);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string; icon: any }> = {
      PENDING_REVIEW: { bg: '#fef3c7', color: '#92400e', icon: Clock },
      APPROVED: { bg: '#d1fae5', color: '#065f46', icon: CheckCircle },
      REJECTED: { bg: '#fecaca', color: '#991b1b', icon: XCircle },
      IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af', icon: AlertCircle },
      NOT_STARTED: { bg: '#f3f4f6', color: '#374151', icon: Users },
      INCOMPLETE: { bg: '#fed7aa', color: '#92400e', icon: AlertCircle }
    };

    const style = styles[status] || styles.NOT_STARTED;
    const Icon = style.icon;

    return (
      <span
        className="status-badge"
        style={{ background: style.bg, color: style.color }}
      >
        <Icon size={14} />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getProgressColor = (percentage: number) => {
    if (percentage === 100) return '#10b981';
    if (percentage >= 75) return '#3b82f6';
    if (percentage >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <Navigation />
      <div className="onboarding-list-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <Users size={32} className="header-icon" />
            <div>
              <h1>Driver Onboarding</h1>
              <p>Manage and approve driver applications</p>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleRefresh} className="btn-icon" title="Refresh">
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Total Applications</div>
              <div className="stat-value">{stats.total}</div>
            </div>
            <div className="stat-card highlight">
              <div className="stat-label">Pending Review</div>
              <div className="stat-value">{stats.pendingReview}</div>
              <div className="stat-badge">Needs Action</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">In Progress</div>
              <div className="stat-value">{stats.inProgress}</div>
            </div>
            <div className="stat-card success">
              <div className="stat-label">Approved</div>
              <div className="stat-value">{stats.approved}</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-filter ${showFilters ? 'active' : ''}`}
          >
            <Filter size={20} />
            Filters
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING_REVIEW">Pending Review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="INCOMPLETE">Incomplete</option>
              </select>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Onboarding Table */}
        {loading ? (
          <div className="table-loading">
            <div className="spinner"></div>
            <p>Loading applications...</p>
          </div>
        ) : onboardings.length === 0 ? (
          <div className="table-empty">
            <Users size={48} />
            <p>No onboarding applications found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="onboarding-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Current Step</th>
                  <th>Started</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {onboardings.map((onboarding) => (
                  <tr key={onboarding.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {onboarding.user.profileImage ? (
                            <img src={onboarding.user.profileImage} alt={onboarding.user.firstName} />
                          ) : (
                            <div className="avatar-placeholder">
                              {onboarding.user.firstName[0]}
                              {onboarding.user.lastName[0]}
                            </div>
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            {onboarding.user.firstName} {onboarding.user.lastName}
                          </div>
                          <div className="user-email">{onboarding.user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{getStatusBadge(onboarding.status)}</td>
                    <td>
                      <div className="progress-cell">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${onboarding.completionPercentage}%`,
                              background: getProgressColor(onboarding.completionPercentage)
                            }}
                          />
                        </div>
                        <span className="progress-text">{onboarding.completionPercentage}%</span>
                      </div>
                    </td>
                    <td>
                      <span className="step-badge">Step {onboarding.currentStep}/8</span>
                    </td>
                    <td>{onboarding.startedAt ? formatDate(onboarding.startedAt) : 'Not started'}</td>
                    <td>{formatDate(onboarding.updatedAt)}</td>
                    <td>
                      <button
                        onClick={() => handleViewDetails(onboarding.userId)}
                        className="btn-icon"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {onboardings.length} of {total} applications
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="btn-icon"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="page-number">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="btn-icon"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingListPage;